import { IntersectionFade } from "../components/IntersectionFade";

type Cell = "yes" | "no" | string;

interface Row {
  capability: string;
  kynetra: Cell;
  xibo: Cell;
  anthias: Cell;
  pisignage: Cell;
  signageos: Cell;
}

const ROWS: Row[] = [
  {
    capability:  "Free cross-platform player (Android/Pi/TV/Desktop)",
    kynetra:     "yes",
    xibo:        "no",
    anthias:     "Pi only",
    pisignage:   "no",
    signageos:   "no",
  },
  {
    capability:  "Push + poll hybrid sync",
    kynetra:     "yes",
    xibo:        "XMR (ZeroMQ)",
    anthias:     "no",
    pisignage:   "Poll only",
    signageos:   "Poll only",
  },
  {
    capability:  "True offline-first playback",
    kynetra:     "yes",
    xibo:        "yes",
    anthias:     "Single-screen only",
    pisignage:   "yes",
    signageos:   "yes",
  },
  {
    capability:  "Free proof-of-play analytics",
    kynetra:     "yes",
    xibo:        "yes",
    anthias:     "no",
    pisignage:   "Paid add-on",
    signageos:   "Paid platform",
  },
  {
    capability:  "Modern stack (TypeScript, no SOAP/XML)",
    kynetra:     "yes",
    xibo:        "no",
    anthias:     "Python/Flask",
    pisignage:   "Angular",
    signageos:   "yes",
  },
  {
    capability:  "Edge-native (Cloudflare Workers / KV / R2)",
    kynetra:     "yes",
    xibo:        "no",
    anthias:     "no",
    pisignage:   "no",
    signageos:   "no",
  },
  {
    capability:  "Multi-store / multi-tenant CMS",
    kynetra:     "yes",
    xibo:        "yes",
    anthias:     "no",
    pisignage:   "Limited",
    signageos:   "yes",
  },
  {
    capability:  "Open-source (permissive or copyleft)",
    kynetra:     "AGPL-3.0",
    xibo:        "AGPL-3.0",
    anthias:     "yes",
    pisignage:   "no",
    signageos:   "Partial",
  },
];

function CellValue({ value }: { value: Cell }) {
  if (value === "yes") return <span className="check-yes" aria-label="Yes">✓</span>;
  if (value === "no")  return <span className="check-no"  aria-label="No">✗</span>;
  return <span className="check-partial">{value}</span>;
}

export function Compare() {
  return (
    <section className="section" id="compare" aria-labelledby="compare-heading">
      <div className="container">
        <IntersectionFade>
          <div className="section-header">
            <span className="eyebrow">Comparison</span>
            <h2 className="h2" id="compare-heading">
              How Kynetra stacks up{" "}
              <span className="grad-text">against the field</span>
            </h2>
            <p className="lead">
              We studied every major open-source and commercial alternative. Here&apos;s the honest matrix.
            </p>
          </div>
        </IntersectionFade>

        <IntersectionFade delay={100}>
          <div className="compare-wrap" role="region" aria-label="Comparison table">
            <table className="compare-table">
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  <th scope="col" className="highlight-col">Kynetra</th>
                  <th scope="col">Xibo</th>
                  <th scope="col">Anthias</th>
                  <th scope="col">PiSignage</th>
                  <th scope="col">signageOS</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map(({ capability, kynetra, xibo, anthias, pisignage, signageos }) => (
                  <tr key={capability}>
                    <td>{capability}</td>
                    <td className="highlight-col"><CellValue value={kynetra} /></td>
                    <td><CellValue value={xibo} /></td>
                    <td><CellValue value={anthias} /></td>
                    <td><CellValue value={pisignage} /></td>
                    <td><CellValue value={signageos} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="compare-caption">
            Data reflects publicly available documentation and feature pages as of June 2026.
            Competitor capabilities may have changed; verify independently before making purchasing decisions.
          </p>
        </IntersectionFade>
      </div>
    </section>
  );
}
